import { Box, Flex, IconButton, Text } from "@chakra-ui/react";
import { Car, ChevronRight, X } from "lucide-react";
import { brandColors, fontSizes, fontWeights, radii } from "@core/theme";
import type { ShopForVehicleSelection } from "../hooks/useShopForVehicle";

export type ShopForVehicleBannerProps = {
  selection: ShopForVehicleSelection | null;
  onManage: () => void;
  onClear: () => void;
};

export const ShopForVehicleBanner = ({
  selection,
  onManage,
  onClear,
}: ShopForVehicleBannerProps) => {
  if (selection === null) {
    return (
      <Flex
        as="button"
        type="button"
        onClick={onManage}
        position="relative"
        overflow="hidden"
        w="100%"
        align="center"
        gap={3}
        textAlign="left"
        p={3}
        pl={4}
        borderRadius={`${radii.md}px`}
        borderWidth={1}
        borderColor={brandColors.border[300]}
        bg="white"
        transition="border-color 0.15s ease, box-shadow 0.15s ease"
        _hover={{
          borderColor: brandColors.primary[300],
          boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
        }}
      >
        <Box
          position="absolute"
          left={0}
          top={0}
          bottom={0}
          w="3px"
          bg={brandColors.primary[500]}
        />
        <Flex
          align="center"
          justify="center"
          boxSize="32px"
          borderRadius="full"
          bg={brandColors.primary[50]}
          borderWidth={1}
          borderColor={brandColors.primary[200]}
          flexShrink={0}
        >
          <Car size={16} color={brandColors.primary[600]} strokeWidth={2.25} />
        </Flex>
        <Box flex={1} minW={0}>
          <Text
            fontSize={fontSizes.sm}
            fontWeight={fontWeights.bold}
            color={brandColors.gray[900]}
            noOfLines={1}
          >
            Shop for your vehicle
          </Text>
          <Text
            fontSize={fontSizes.xs}
            color={brandColors.gray[500]}
            mt={0.5}
            noOfLines={2}
            lineHeight="1.35"
          >
            Add your car to see parts that fit
          </Text>
        </Box>
        <Flex
          align="center"
          gap={0.5}
          flexShrink={0}
          px={3}
          py={1.5}
          borderRadius="full"
          bg={brandColors.primary[600]}
          color="white"
          fontSize={fontSizes.xs}
          fontWeight={fontWeights.bold}
        >
          Add vehicle
          <ChevronRight size={14} color="white" />
        </Flex>
      </Flex>
    );
  }

  const vehicleLabel =
    selection.subtitle === null ? selection.title : `${selection.title} · ${selection.subtitle}`;

  return (
    <Flex
      position="relative"
      overflow="hidden"
      w="100%"
      align="center"
      gap={3}
      p={3}
      pl={4}
      pr={2}
      borderRadius={`${radii.md}px`}
      borderWidth={1}
      borderColor={brandColors.success[500]}
      bg={brandColors.success[50]}
    >
      <Box position="absolute" left={0} top={0} bottom={0} w="3px" bg={brandColors.success[600]} />
      <Flex
        as="button"
        type="button"
        align="center"
        gap={3}
        flex={1}
        minW={0}
        textAlign="left"
        onClick={onManage}
        _hover={{ opacity: 0.92 }}
      >
        <Flex
          align="center"
          justify="center"
          boxSize="32px"
          borderRadius="full"
          bg={brandColors.success[600]}
          flexShrink={0}
        >
          <Car size={16} color="#ffffff" strokeWidth={2.25} />
        </Flex>
        <Box flex={1} minW={0}>
          <Text
            fontSize={fontSizes.xs}
            fontWeight={fontWeights.bold}
            letterSpacing="0.04em"
            color={brandColors.success[700]}
            textTransform="uppercase"
          >
            Shopping for
          </Text>
          <Text
            fontSize={fontSizes.sm}
            fontWeight={fontWeights.bold}
            color={brandColors.gray[900]}
            noOfLines={2}
            lineHeight="1.35"
            mt={0.5}
          >
            {vehicleLabel}
          </Text>
        </Box>
        <Box flexShrink={0}>
          <ChevronRight size={18} color={brandColors.gray[500]} />
        </Box>
      </Flex>
      <IconButton
        aria-label="Clear vehicle"
        icon={<X size={16} />}
        size="xs"
        variant="ghost"
        borderRadius="full"
        minW="32px"
        h="32px"
        color={brandColors.gray[600]}
        onClick={onClear}
        _hover={{ bg: "white", color: brandColors.primary[600] }}
      />
    </Flex>
  );
};
